import axiosInstance from "../utils/axios";

// Submit category suggestion API
export const submitCategorySuggestionAPI = async (payload: { title: string; description: string }) => {
  try {
    const response = await axiosInstance.post("/api/user/category-suggestion/create", payload);
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    console.error("Error submitting category suggestion:", error);
    throw new Error(error.response?.data?.message || "Failed to submit category suggestion");
  }
};

const getUser = async () => {
  try {
    const response = await axiosInstance.get("/api/user/auth/get-profile");
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data.data;
  } catch (error: any) {
    console.error("Error fetching user:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch user");
  }
};

export const updateUserProfileAPI = async (formData: FormData) => {
  try {
    const response = await axiosInstance.post("/api/user/auth/update-profile", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    console.error("Error updating user profile:", error);
    throw new Error(error.response?.data?.message || "Failed to update user profile");
  }
};

export const getGameCategoryListAPI = async (params?: { search?: string; page?: number }) => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.search) {
      queryParams.append("search", params.search);
    }
    if (params?.page) {
      queryParams.append("page", params.page.toString());
    }

    const queryString = queryParams.toString();
    const url = `/api/user/game-category/list${queryString ? `?${queryString}` : ""}`;

    const response = await axiosInstance.get(url);
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    console.error("Error fetching game category list:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch game category list");
  }
};

export const getCategoryBadgeListAPI = async () => {
  try {
    const response = await axiosInstance.get("/api/user/category-badge/list");
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    console.error("Error fetching category badge list:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch category badge list");
  }
};

export const clearNewAchievementEarnedAPI = async () => {
  try {
    const response = await axiosInstance.post("/api/user/auth/clear-new-achievement-earned");
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    console.error("Error clearing new achievement earned flag:", error);
    throw new Error(error.response?.data?.message || "Failed to clear new achievement earned flag");
  }
};

export interface CreateInquiryPayload {
  name: string;
  email: string;
  message: string;
}

export const createInquiryAPI = async (payload: CreateInquiryPayload) => {
  try {
    const response = await axiosInstance.post("/api/user/inquiry/create-inquiry", payload);
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    console.error("Error creating inquiry:", error);
    throw new Error(error.response?.data?.message || "Failed to submit inquiry");
  }
};

export const getLeaderboardListAPI = async () => {
  try {
    const response = await axiosInstance.get("/api/user/leaderboard/list");
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    console.error("Error fetching leaderboard list:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch leaderboard list");
  }
};

export interface ReportQuestionPayload {
  question_id: string;
  question_type: string;
  reason: string;
}

export const reportQuestionAPI = async (payload: ReportQuestionPayload) => {
  try {
    const response = await axiosInstance.post("/api/user/reported-question/report-question", payload, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    console.error("Error reporting question:", error);
    throw new Error(error.response?.data?.message || "Failed to report question");
  }
};

export const getReportQuestionReasonsAPI = async () => {
  try {
    const response = await axiosInstance.get("/api/user/game/report-question-reasons");
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    console.error("Error fetching report question reasons:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch report question reasons");
  }
};

export const getHowToPlayStepListAPI = async () => {
  try {
    const response = await axiosInstance.get("/api/user/how-to-play-step/list");
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    console.error("Error fetching how to play step list:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch how to play step list");
  }
};

export const getMyRankAPI = async () => {
  try {
    const response = await axiosInstance.get("/api/user/leaderboard/my-rank");
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    console.error("Error fetching my rank:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch my rank");
  }
};

// Submit support request API
export interface SubmitSupportPayload {
  description: string;
}

export const submitSupportRequestAPI = async (payload: SubmitSupportPayload) => {
  try {
    const response = await axiosInstance.post("/api/user/support-ticket/create-support-ticket", payload);
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    console.error("Error submitting support request:", error);
    throw new Error(error.response?.data?.message || "Failed to submit support request");
  }
};

export interface GetCharacterListParams {
  type?: string;
  gender?: string;
}

export const getCharacterListAPI = async (params?: GetCharacterListParams) => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.type) {
      queryParams.append("type", params.type);
    }
    if (params?.gender) {
      queryParams.append("gender", params.gender);
    }
    const queryString = queryParams.toString();
    const url = `/api/user/character/list${queryString ? `?${queryString}` : ""}`;

    const response = await axiosInstance.get(url);
    if (!response.data.status) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    console.error("Error fetching character list:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch character list");
  }
};

export default getUser;
